import { BedDouble, Bath, Ruler, Home, Check, MessageCircle, Info, MapPin } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { waHref } from "@/lib/wa";

const UnitCard = ({ unit, index, cta }) => (
  <Reveal delay={index * 0.12}>
    <article
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(0,86,179,0.12)]"
      data-testid={`unit-card-${index}`}
    >
      <div className="relative overflow-hidden">
        <img
          src={unit.image}
          alt={`Rumah Type 36 ${unit.name} — perumahan cluster Pontianak`}
          className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute left-4 top-4 rounded-full bg-brand px-3.5 py-1 text-xs font-bold text-white shadow" data-testid={`unit-type-${index}`}>
          Type 36
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <h3 className="font-display text-xl font-bold tracking-tight text-brand-dark" data-testid={`unit-name-${index}`}>
          {unit.name}
        </h3>
        {unit.location && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500" data-testid={`unit-location-${index}`}>
            <MapPin className="h-4 w-4 shrink-0 text-brand" /> {unit.location}
          </p>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-brand-mist p-3.5" data-testid={`unit-land-${index}`}>
            <Ruler className="h-4 w-4 text-brand" />
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Luas Tanah</p>
            <p className="font-display text-sm font-bold text-brand-dark">{unit.landArea}</p>
          </div>
          <div className="rounded-xl bg-brand-mist p-3.5" data-testid={`unit-building-${index}`}>
            <Home className="h-4 w-4 text-brand" />
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Luas Bangunan</p>
            <p className="font-display text-sm font-bold text-brand-dark">{unit.buildingArea}</p>
          </div>
          <div className="rounded-xl bg-brand-mist p-3.5" data-testid={`unit-bedrooms-${index}`}>
            <BedDouble className="h-4 w-4 text-brand" />
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Kamar Tidur</p>
            <p className="font-display text-sm font-bold text-brand-dark">{unit.bedrooms}</p>
          </div>
          <div className="rounded-xl bg-brand-mist p-3.5" data-testid={`unit-bathrooms-${index}`}>
            <Bath className="h-4 w-4 text-brand" />
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Kamar Mandi</p>
            <p className="font-display text-sm font-bold text-brand-dark">{unit.bathrooms}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {(unit.features || []).map((f) => (
            <span key={f} className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 px-3.5 py-1.5 text-xs font-medium text-brand-dark">
              <Check className="h-3 w-3 text-brand" /> {f}
            </span>
          ))}
        </div>
        {unit.dp && (
          <div className="mt-4 rounded-xl border border-brand/20 bg-brand-mist p-4" data-testid={`unit-dp-${index}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Uang Muka (DP)</p>
            <p className="mt-1 font-display text-sm font-bold text-brand-dark">{unit.dp}</p>
            {unit.dpNote && (
              <p className="mt-2 text-xs leading-relaxed text-slate-500" data-testid={`unit-dp-note-${index}`}>
                <Info className="mr-1 inline h-3.5 w-3.5 text-brand" />
                {unit.dpNote}
              </p>
            )}
          </div>
        )}
        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Harga</p>
            <p className="font-display text-xl font-extrabold text-brand" data-testid={`unit-price-${index}`}>{unit.price}</p>
          </div>
          <a
            href={cta}
            target={cta.startsWith("http") ? "_blank" : undefined}
            rel="noopener"
            data-testid={`unit-cta-${index}`}
            className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-deep active:scale-95"
          >
            <MessageCircle className="h-4 w-4" />
            Tanya Unit
          </a>
        </div>
      </div>
    </article>
  </Reveal>
);

export default function TypeHouse({ content }) {
  const units = content.typeHouse?.units || [];
  const cta = waHref(content);

  return (
    <section id="tipe-rumah" className="bg-fade-blue-up py-24" data-testid="type-house-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          number="03"
          eyebrow="Detail Unit"
          title="Rumah Type 36 Pontianak"
          description="Dua pilihan unit Type 36 dengan tata ruang efisien — 2 kamar tidur, 1 kamar mandi, carport, ruang keluarga, dan taman."
        />
        <div className="grid gap-8 md:grid-cols-2">
          {units.map((u, i) => (
            <UnitCard key={i} unit={u} index={i} cta={cta} />
          ))}
        </div>
        <Reveal delay={0.2} className="mt-10 text-center">
          <a
            href={cta}
            target={cta.startsWith("http") ? "_blank" : undefined}
            rel="noopener"
            data-testid="type36-availability-button"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brand px-8 py-3.5 text-sm font-semibold text-brand transition-colors duration-200 hover:bg-brand hover:text-white active:scale-95"
          >
            <MessageCircle className="h-4 w-4" />
            Tanyakan Ketersediaan Unit
          </a>
        </Reveal>
      </div>
    </section>
  );
}
